import type { FC, ReactNode } from 'react'
import type { ProductSummary } from '@/types'

import Link from 'next/link'
import { ArrowRight, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button, buttonVariants } from '@/components/ui/button'
import Price from '@/components/shop/price'
import ProductImage from '@/components/shop/product-image'
import { useCart } from '@/components/shop/cart-provider'

interface ProductCardProps {
    product: ProductSummary
    className?: string
}

const ProductCard: FC<ProductCardProps> = ({ product, className }): ReactNode => {
    const { addItem } = useCart()
    const href = `/shop/${product.slug}`
    const onSale = product.compare_at_price !== null && product.compare_at_price > product.price
    const needsChoice = product.options.length > 0

    return (
        <article
            className={cn(
                'group relative flex flex-col overflow-hidden rounded-3xl bg-card ring-1 ring-border transition duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/10',
                className
            )}
        >
            <div className='relative aspect-square overflow-hidden'>
                <ProductImage
                    src={product.image}
                    alt={product.image_alt ?? product.name}
                    categorySlug={product.category_slug}
                    className='transition duration-500 group-hover:scale-[1.03]'
                />

                <div className='absolute top-3 left-3 flex flex-wrap gap-1.5'>
                    {!product.in_stock && (
                        <Badge variant='outline'>
                            Sold out
                        </Badge>
                    )}

                    {product.in_stock && onSale && (
                        <Badge variant='accent'>
                            Sale
                        </Badge>
                    )}
                </div>
            </div>

            <div className='flex flex-1 flex-col p-5'>
                {product.category_id && product.category_name && (
                    <p className='text-xs font-bold tracking-[0.14em] text-tide uppercase'>
                        <span data-mdlfy-cms={`categories:${product.category_id}:name`}>
                            {product.category_name}
                        </span>
                    </p>
                )}

                <h3 className='mt-2 text-lg leading-snug font-bold'>
                    <Link
                        href={href}
                        className='after:absolute after:inset-0 after:rounded-3xl focus-visible:outline-none focus-visible:after:ring-3 focus-visible:after:ring-ring/50'
                    >
                        <span data-mdlfy-cms={`products:${product.id}:name`}>
                            {product.name}
                        </span>
                    </Link>
                </h3>

                {product.tagline && (
                    <p
                        data-mdlfy-cms={`products:${product.id}:tagline`}
                        className='mt-2 line-clamp-2 text-sm leading-relaxed text-muted-foreground'
                    >
                        {product.tagline}
                    </p>
                )}

                <div className='mt-auto flex items-end justify-between gap-3 pt-5'>
                    <Price price={product.price} compareAt={product.compare_at_price} />

                    {product.in_stock && !needsChoice && (
                        <Button
                            size='icon'
                            variant='accent'
                            onClick={() => addItem(product, null, 1)}
                            aria-label={`Add ${product.name} to cart`}
                            className='relative z-10'
                        >
                            <Plus className='size-5' />
                        </Button>
                    )}

                    {product.in_stock && needsChoice && (
                        <Link
                            href={href}
                            tabIndex={-1}
                            aria-hidden='true'
                            className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'relative z-10')}
                        >
                            Choose
                            <ArrowRight />
                        </Link>
                    )}
                </div>
            </div>
        </article>
    )
}

export default ProductCard
